import {useEffect, useMemo, useRef, useState} from 'react';
import {
  AccordionItemV2,
  Button,
  Icon,
  LoadingSpinner,
  Modal,
  Tab,
  TabsNavigation,
  TextInput,
  Toggle,
  Tooltip,
} from 'pepsico-ds';
import AccordionHoc from '../../hoc/AccordionHoc';
import {DropdownItemOption} from '../../hoc/DropdownHoc';
import AiChatFooterHoc from '../../hoc/AiChatFooterHoc';
import {MarkdownRenderV2} from './MarkdownRenderV2';
import {ChatMessageItem} from './ChatMessageItem';
import useSupervisorChat from './useSupervisorChat';
import useInsights from '../../hooks/useInsights';
import {useAppSelector} from '../../../store/Store';
import {getUserName} from '../../../store/AuthSlice';
import {getmessageListSupervisor} from '../../../store/SupervisorChatSlice';
import {
  BrandIcon,
  BrandMark,
  BrandName,
  ChatList,
  DescriptionText,
  DisclaimerText,
  FilterRow,
  FooterSection,
  HeaderDivider,
  GreetingIcon,
  GreetingSection,
  GreetingText,
  HeaderActions,
  InsightButton,
  InsightErrorState,
  InsightLoadingState,
  InsightSection,
  MemoryToggleWrap,
  MessagesSection,
  PanelWrapper,
  PromptTag,
  ScrollContent,
  ScrollTopContent,
  StickyBar,
  StickyBarContent,
  StyledAccordion,
  SuggestionToggle,
  SuggestionsSection,
  RecentChatsList,
  RecentChatsRow,
  TabContent,
  TabsSection,
  TokenModalBody,
} from './index.style';

interface AiChatPanelProps {
  onClose: () => void;
  isFullScreen: boolean;
  onToggleFullScreen: () => void;
}

interface InsightTabContent {
  id: string;
  header: string;
  content: string;
}

type ComposerInputElement = HTMLTextAreaElement | HTMLInputElement;

const insightOptions: DropdownItemOption[] = [
  {id: 'travel_and_entertainment', displayText: 'T&E Insights'},
  {id: 'contractor_and_temp_labor', displayText: 'TLC Insights'},
  {id: 'general_supplies_and_services', displayText: 'GSS Insights'},
  {id: 'consulting_engagement', displayText: 'Consulting Insights'},
  {id: 'tech_devices', displayText: 'Tech Devices Insights'},
];

const quickPrompts = [
  'What is my team`s average spending on air tickets vs. average of the Sector ?',
  'Show me my top three travel savings opportunities ?',
  'Show me my year over year spend within General Supplies & Services ?',
  'What if a preferred supplier is not available for what I need ?',
];

const recentChats = [
  'Show me my top three travel savings opportunities?',
  'What is my team spending on air tickets vs. sector average?',
  'Show year over year spend within General Supplies & Services?',
  'What if a preferred supplier is not available for what I need?',
  'Where are we overspending on business travel this quarter?',
  'Are we compliant with preferred hotel suppliers?',
  'Compare marketing spend vs budget by region',
  'Which categories have the highest cost variance?',
];

const getTimeOfDayGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
};

const AiChatPanel = ({isFullScreen}: AiChatPanelProps) => {
  const panelRef = useRef<HTMLElement | null>(null);
  const scrollContentRef = useRef<HTMLDivElement | null>(null);
  const chatListRef = useRef<HTMLDivElement | null>(null);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const suppressShowRef = useRef(false);
  const {
    handleSendQuestionGenie,
    handleClearChat,
    handleStopChat,
    isMemoryEnabled,
    toggleMemory,
    isAgentEnabled,
    toggleAgent,
  } = useSupervisorChat();
  const messagesGenieRaw = useAppSelector<any>(getmessageListSupervisor);
  const messagesGenie = useMemo(
    () => (messagesGenieRaw as any[] | undefined) ?? [],
    [messagesGenieRaw],
  );
  const userName = useAppSelector(getUserName);

  const [selectedInsightId, setSelectedInsightId] = useState<string>('');

  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [isInsightsExpanded, setIsInsightsExpanded] = useState(true);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [hasDraftText, setHasDraftText] = useState(false);
  const [isChatAreaActive, setIsChatAreaActive] = useState(false);
  const [isMoreOptionsOpen, setIsMoreOptionsOpen] = useState(false);
  const [activeScreen, setActiveScreen] = useState<'chat' | 'recentChats'>(
    'chat',
  );
  const [isTokenModalOpen, setIsTokenModalOpen] = useState(false);
  const [tokenValue, setTokenValue] = useState('');
  const [tokenError, setTokenError] = useState('');

  // Map messagesGenie (from Redux store) into the shape AIChatItems expects.
  // Switches off the typewriter "Thinking..." loader as soon as the stream's
  // live-trace has at least one step, so <ChatMessageItem> renders
  // <LiveAgentTrace> instead while the request is still in flight.
  const genieChatMessages = useMemo(
    () =>
      (messagesGenie || []).map((msg: any, idx: number) => ({
        id: msg.request_id || `genie-${idx}`,
        sender: msg.sender,
        content: msg.isLoading
          ? msg.liveTrace?.steps?.length > 0
            ? ''
            : 'Thinking...'
          : msg.text || '',
        timestamp: new Date(),
        raw: msg,
      })),
    [messagesGenie],
  );

  const hasMessages = genieChatMessages.length > 0;
  const lastMessage = messagesGenie.at(-1);
  const isChatInProcess = Boolean(lastMessage?.isLoading);
  const shouldShowGreeting =
    !hasMessages && !isChatInProcess && !hasDraftText && !isChatAreaActive;
  const shouldShowSuggestions =
    showSuggestions && messagesGenie.length <= 1 && !isChatInProcess;

  const {
    introContent: insightIntroContent,
    sections: insightSections,
    loading: insightsLoading,
    error: insightsError,
    refetch: refetchInsights,
  } = useInsights({module: selectedInsightId});

  const tabsData: InsightTabContent[] = useMemo(
    () =>
      (insightSections || []).map((section, index) => ({
        id: `${selectedInsightId}-${index}`,
        header: section.header,
        content: section.content,
      })),
    [insightSections, selectedInsightId],
  );

  const activeSection = tabsData[activeTabIndex] ?? null;

  useEffect(() => {
    setActiveTabIndex(0);
  }, [selectedInsightId]);

  // Auto-scroll the chat content to the bottom when a new message is added
  // or when the bot finishes thinking, so the latest message is visible.
  useEffect(() => {
    const el = scrollContentRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [genieChatMessages.length, isChatInProcess]);

  // Keep following the bottom while a response is actively streaming (agent
  // trace steps, typed text, charts) instead of only jumping on message
  // add/remove above — the streamed content grows in place without those
  // deps changing. Only follows while already near the bottom so scrolling
  // up to re-read earlier messages during a stream isn't fought.
  useEffect(() => {
    const scrollEl = scrollContentRef.current;
    const listEl = chatListRef.current;
    if (!scrollEl || !listEl) return undefined;

    const NEAR_BOTTOM_PX = 120;
    const isNearBottom = () =>
      scrollEl.scrollHeight - scrollEl.scrollTop - scrollEl.clientHeight <= NEAR_BOTTOM_PX;

    const observer = new ResizeObserver(() => {
      if (isNearBottom()) {
        scrollEl.scrollTop = scrollEl.scrollHeight;
      }
    });
    observer.observe(listEl);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    // On text input, hide suggestions and activate chat area
    if (hasDraftText) {
      setShowSuggestions(false);
      setIsChatAreaActive(true);
    }
  }, [hasDraftText]);

  useEffect(() => {
    // Hide suggestions/insights only when the composer input is focused or clicked
    const handleComposerInteraction = (event: Event) => {
      const target = event.target as Element | null;
      const isComposerInput = Boolean(
        target?.closest('.footer .field textarea, .footer .field input'),
      );

      if (!isComposerInput) return;

      setShowSuggestions(false);
      setIsInsightsExpanded(false);
      setIsChatAreaActive(true);
    };

    const root = panelRef.current;
    if (root) {
      root.addEventListener('click', handleComposerInteraction);
      root.addEventListener('focusin', handleComposerInteraction);
      return () => {
        root.removeEventListener('click', handleComposerInteraction);
        root.removeEventListener('focusin', handleComposerInteraction);
      };
    }
  }, []);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      const root = panelRef.current;
      const target = event.target as Node | null;
      if (!root || !target) return;
      if (!root.contains(target)) return;

      const isMenuTriggerOrMenu =
        target instanceof Element &&
        Boolean(
          target.closest('.ai-chat-more-options, .ai-chat-more-options-menu'),
        );

      if (!isMenuTriggerOrMenu) {
        setIsMoreOptionsOpen(false);
      }
    };

    document.addEventListener('click', handleOutsideClick);
    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, []);

  const scheduleShowSuggestions = () => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      if (!isChatAreaActive) {
        setShowSuggestions(true);
        setIsInsightsExpanded(true);
      }
    }, 10000);
  };

  const sendMessage = (message: string) => {
    const trimmedMessage = message.trim();
    if (!trimmedMessage) return;

    suppressShowRef.current = true;
    setIsChatAreaActive(true);
    setHasDraftText(false);
    setShowSuggestions(false);
    setIsInsightsExpanded(false);
    handleSendQuestionGenie(trimmedMessage);
    scheduleShowSuggestions();
  };

  const fillTextArea = (text: string) => {
    const root = panelRef.current;
    if (!root) return;
    const input = root.querySelector(
      '.footer .field textarea, .footer .field input',
    ) as ComposerInputElement | null;
    if (!input) return;

    // Set value via React's internal setter so controlled components update
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
      Object.getPrototypeOf(input),
      'value',
    )?.set;
    nativeInputValueSetter?.call(input, text);
    input.dispatchEvent(new Event('input', {bubbles: true}));
    input.dispatchEvent(new Event('change', {bubbles: true}));
    input.focus();

    setShowSuggestions(false);
    setIsInsightsExpanded(false);
    scheduleShowSuggestions();
  };

  const handleNewChat = () => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    handleClearChat({} as unknown as React.MouseEvent<HTMLElement>);
    setIsChatAreaActive(false);
    setHasDraftText(false);
    setShowSuggestions(true);
    setIsInsightsExpanded(true);
    setActiveScreen('chat');
    setIsMoreOptionsOpen(false);
    globalThis.dispatchEvent(new Event('spendwise:newChat'));
  };

  const handleRecentChatClick = (prompt: string) => {
    setActiveScreen('chat');
    setIsMoreOptionsOpen(false);
    setTimeout(() => fillTextArea(prompt), 0);
  };

  const handleOpenTokenModal = () => {
    setTokenValue(sessionStorage.getItem('prodOktaToken') || '');
    setTokenError('');
    setIsTokenModalOpen(true);
  };

  const handleCloseTokenModal = () => {
    setIsTokenModalOpen(false);
    setTokenError('');
  };

  const handleTokenSubmit = () => {
    const trimmedToken = tokenValue.trim();
    if (!trimmedToken) {
      setTokenError('Token is required');
      return;
    }
    sessionStorage.setItem('prodOktaToken', trimmedToken);
    setTokenError('');
    setIsTokenModalOpen(false);
  };

  const renderInsightsBody = () => {
    if (insightsLoading) {
      return (
        <InsightLoadingState>
          <LoadingSpinner
            speed='medium'
            size={{width: '36px', height: '36px'}}
          />
        </InsightLoadingState>
      );
    }

    if (insightsError) {
      return (
        <InsightErrorState
          style={{display: 'flex', alignItems: 'center', gap: '1rem'}}
        >
          <span>{insightsError}</span>
          <Button
            text='Retry'
            size='small'
            variant='primary'
            onClick={refetchInsights}
            style={{marginLeft: 8, padding: '0.25rem 1rem'}} // Add this line
          />
        </InsightErrorState>
      );
    }

    if (!selectedInsightId) {
      return (
        <DescriptionText $isFullScreen={isFullScreen}>
          Select an insight category above to generate AI insights.
        </DescriptionText>
      );
    }

    return (
      <>
        {insightIntroContent && (
          <DescriptionText $isFullScreen={isFullScreen}>
            {insightIntroContent}
          </DescriptionText>
        )}
        <TabsSection $isFullScreen={isFullScreen}>
          <TabsNavigation
            type='line'
            selectedIndex={activeTabIndex}
            onUpdate={(index) => setActiveTabIndex(index)}
          >
            {tabsData.map((tab) => (
              <Tab
                key={tab.id}
                label={tab.header}
                isActive={tab.id === tabsData[activeTabIndex]?.id}
                onClick={() => handleInsightTabClick(tab.id)}
              />
            ))}
          </TabsNavigation>

          {activeSection && (
            <TabContent $isFullScreen={isFullScreen}>
              <MarkdownRenderV2 content={activeSection.content} />
            </TabContent>
          )}
        </TabsSection>
      </>
    );
  };

  const handleInsightTabClick = (tabId: string) => {
    setActiveTabIndex(tabsData.findIndex((section) => section.id === tabId));
  };
  console.log("msg in AiChatPanel",genieChatMessages);

  return (
    <PanelWrapper
      ref={panelRef}
      $isFullScreen={isFullScreen}
    >
      <Modal
        showModal={isTokenModalOpen}
        onCloseModal={handleCloseTokenModal}
        title='Enter Prod Okta Token'
        type='S'
        showTertiaryButton={false}
        showLink={false}
        showAuxiliaryButton={false}
        showSecondaryButton={false}
        closeOnOutsideClick
        primaryButtonProps={{
          text: 'Submit',
          variant: 'primary',
          size: 'medium',
          onClick: handleTokenSubmit,
        }}
        text={
          <TokenModalBody>
            <TextInput
              label={{label: 'Prod Okta Token'}}
              required
              isPassword
              placeholderText='Enter your token'
              value={tokenValue}
              onUpdate={(value) => {
                setTokenValue(value);
                if (tokenError) setTokenError('');
              }}
              error={tokenError}
              feedbackType={tokenError ? 'error' : undefined}
            />
          </TokenModalBody>
        }
      />

      <StickyBar $isFullScreen={isFullScreen}>
        <StickyBarContent>
          <BrandMark>
            <BrandIcon>
              <Icon
                icon='auto_awesome'
                size='small'
                alt='SpendWise Assistant'
              />
            </BrandIcon>
            <BrandName>SpendWise Assistant</BrandName>
          </BrandMark>

          <HeaderActions>
            <Tooltip
              className=''
              direction='top-left'
              hideToolTipDelayDuration={150}
              maxWidth='70px'
              showToolTipDelayDuration={300}
              text='New Chat'
            >
              <Button
                text='New chat'
                iconLeading='add'
                size='medium'
                variant='secondary'
                onClick={handleNewChat}
              />
            </Tooltip>

            <HeaderDivider />

            <Tooltip
              className=''
              direction='top-left'
              hideToolTipDelayDuration={150}
              maxWidth='140px'
              showToolTipDelayDuration={300}
              text='Answer from memory-backed API'
            >
              <MemoryToggleWrap>
                <Toggle
                  checked={isMemoryEnabled}
                  size='medium'
                  onUpdate={toggleMemory}
                  label=''
                  value='Memory'
                  variant='horizontal'
                  showCheckedIcon={false}
                />
              </MemoryToggleWrap>
            </Tooltip>

            <Tooltip
              className=''
              direction='top-left'
              hideToolTipDelayDuration={150}
              maxWidth='140px'
              showToolTipDelayDuration={300}
              text='Use the nl2sql engine instead of Genie'
            >
              <MemoryToggleWrap>
                <Toggle
                  checked={isAgentEnabled}
                  size='medium'
                  onUpdate={toggleAgent}
                  label=''
                  value='Custom Agent'
                  variant='horizontal'
                  showCheckedIcon={false}
                />
              </MemoryToggleWrap>
            </Tooltip>

            <Tooltip
              className=''
              direction='top-left'
              hideToolTipDelayDuration={150}
              maxWidth='120px'
              showToolTipDelayDuration={300}
              text='Set prod okta token'
            >
              <Button
                text='Set Token'
                iconLeading='vpn_key'
                size='medium'
                variant='secondary'
                onClick={handleOpenTokenModal}
              />
            </Tooltip>
          </HeaderActions>
        </StickyBarContent>
      </StickyBar>

      <ScrollContent
        ref={scrollContentRef}
        $isFullScreen={isFullScreen}
      >
        {activeScreen === 'recentChats' ? (
          <RecentChatsList $isFullScreen={isFullScreen}>
            {recentChats.map((chat) => (
              <RecentChatsRow
                key={chat}
                onClick={() => handleRecentChatClick(chat)}
              >
                {chat}
              </RecentChatsRow>
            ))}
          </RecentChatsList>
        ) : (
          <>
            <ScrollTopContent $isFullScreen={isFullScreen}>
              {shouldShowGreeting && (
                <GreetingSection $isFullScreen={isFullScreen}>
                  <GreetingIcon />
                  <GreetingText $isFullScreen={isFullScreen}>
                    {`${getTimeOfDayGreeting()}${userName ? `, ${userName}` : ''}`}
                    <br />
                    {'How can I '}
                    <span className='greeting-highlight'>
                      assist you today
                    </span>
                    {'?'}
                  </GreetingText>
                </GreetingSection>
              )}

              {/* <InsightSection $isFullScreen={isFullScreen}>
                <StyledAccordion $isFullScreen={isFullScreen}>
                  <AccordionHoc
                    selectedItemIndex={isInsightsExpanded ? 0 : -1}
                    onUpdate={() =>
                      setIsInsightsExpanded((current) => !current)
                    }
                    presentation='box-shadow'
                    separator={false}
                  >
                    <AccordionItemV2
                      label='AI Insights'
                      expanded={isInsightsExpanded}
                      presentation='box-shadow'
                    >
                      <FilterRow $isFullScreen={isFullScreen}>
                        {insightOptions.map((item) => (
                          <InsightButton
                            key={item.id}
                            text={item.displayText}
                            color='purple'
                            variant={
                              item.id === selectedInsightId
                                ? 'primary'
                                : 'secondary'
                            }
                            $selected={item.id === selectedInsightId}
                            $compact={!isFullScreen}
                            onClick={() => {
                              setSelectedInsightId(item.id);
                              setActiveTabIndex(0);
                            }}
                          />
                        ))}
                      </FilterRow>
                      {renderInsightsBody()}
                    </AccordionItemV2>
                  </AccordionHoc>
                </StyledAccordion>
              </InsightSection> */}
            </ScrollTopContent>

            {!isInsightsExpanded && (
              <MessagesSection $isFullScreen={isFullScreen}>
                <ChatList ref={chatListRef}>
                  {genieChatMessages.map((msg: any) => (
                    <ChatMessageItem
                      key={msg.id}
                      sender={msg.sender}
                      content={msg.content}
                      userName={userName || 'U'}
                      isFullScreen={isFullScreen}
                      msg={msg.raw}
                    />
                  ))}
                </ChatList>
              </MessagesSection>
            )}

            {shouldShowSuggestions && (
              <SuggestionsSection $isFullScreen={isFullScreen}>
                <SuggestionToggle>{'Suggested Questions'}</SuggestionToggle>
                {quickPrompts.map((prompt) => (
                  <PromptTag
                    key={prompt}
                    text={prompt}
                    type='outlined'
                    color='blue'
                    size='large'
                    isCopyable={false}
                    onClick={() => fillTextArea(prompt)}
                  />
                ))}
              </SuggestionsSection>
            )}
          </>
        )}
      </ScrollContent>

      {activeScreen === 'chat' && (
        <FooterSection $isFullScreen={isFullScreen}>
          <div
            className={`footer-shell${isChatInProcess ? ' is-loading' : ''}`}
          >
            <AiChatFooterHoc
              autocompleteItems={quickPrompts}
              chatbotTitle='SpendWise AI Assistant'
              onSendMessage={sendMessage}
              onSuggestionClick={fillTextArea}
              isLoading={isChatInProcess}
              onStop={handleStopChat}
            />

            <DisclaimerText>
              Disclaimer : Always review the accuracy of responses
            </DisclaimerText>
          </div>
        </FooterSection>
      )} 
    </PanelWrapper>
  );
};

export default AiChatPanel;
